import { useCallback } from 'react';
import { Images, Upload } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { isAcceptedReceipt } from '@/lib/receipt-file';
import { FileDropZone } from '@/components/shared/FileDropZone';

const MAX_FILES = 20;
const RECEIPT_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf,.heic,.heif,.pdf';

interface ReceiptDropZoneProps {
  onFilesSelected: (files: File[]) => void;
  onNoReceipt: () => void;
  /**
   * Open the job's photo library. Absent hides the action — an expense entered
   * somewhere without a job's files behind it has nothing to choose from.
   */
  onChooseFromLibrary?: () => void;
  isUploading: boolean;
  uploadProgress?: { completed: number; total: number };
}

export function ReceiptDropZone({
  onFilesSelected,
  onNoReceipt,
  onChooseFromLibrary,
  isUploading,
  uploadProgress,
}: ReceiptDropZoneProps) {
  const { toast } = useToast();

  const handleFiles = useCallback((files: File[]) => {
    const valid = files.filter(isAcceptedReceipt);
    const rejected = files.length - valid.length;

    if (rejected > 0) {
      toast({
        title: 'Some files skipped',
        description: `${rejected} file(s) were not supported (JPG, PNG, WebP, HEIC, or PDF).`,
        variant: 'destructive',
      });
    }

    if (valid.length > MAX_FILES) {
      toast({
        title: 'Too many files',
        description: `Maximum ${MAX_FILES} receipts at a time. Please select fewer files.`,
        variant: 'destructive',
      });
      return;
    }

    if (valid.length > 0) {
      onFilesSelected(valid);
    }
  }, [onFilesSelected, toast]);

  return (
    <div className="mb-4 rounded-lg border bg-gray-50 p-3 sm:p-4">
      <FileDropZone
        onFiles={handleFiles}
        accept={RECEIPT_ACCEPT}
        busy={isUploading}
        // A phone has no drag, so the dashed box is only ever a big button —
        // and 40px of padding on each side is 80px of the screen's width.
        className="p-6 sm:p-10"
        busyContent={
          <>
            <div className="h-8 w-8 mx-auto mb-3 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            <p className="font-semibold text-indigo-700">
              Uploading {uploadProgress ? `${uploadProgress.completed} of ${uploadProgress.total}` : ''} receipt{uploadProgress && uploadProgress.total !== 1 ? 's' : ''}...
            </p>
            <p className="text-sm text-indigo-400 mt-1">This may take a moment</p>
          </>
        }
      >
        <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <p className="font-semibold text-gray-700">
          <span className="sm:hidden">Add receipts</span>
          <span className="hidden sm:inline">Drop receipts here</span>
        </p>
        <p className="mt-1 text-balance text-sm text-gray-400">
          Images (JPG, PNG, WebP, HEIC) or PDF — multiple files supported
        </p>
      </FileDropZone>
      {/* The two ways of not uploading, kept together under the box: the photo
          is already on the job, or there is no photo at all. */}
      <div className="mt-3 flex flex-col items-center gap-1">
        {onChooseFromLibrary && (
          <button
            type="button"
            onClick={onChooseFromLibrary}
            disabled={isUploading}
            className="inline-flex min-h-[44px] items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50 sm:min-h-0"
          >
            <Images className="h-4 w-4" aria-hidden="true" />
            Choose from job photos
          </button>
        )}
        <button
          type="button"
          onClick={onNoReceipt}
          className="inline-flex min-h-[44px] items-center text-sm text-indigo-500 underline hover:text-indigo-700 sm:min-h-0"
        >
          Add expense without a receipt →
        </button>
      </div>
    </div>
  );
}
